import React, { useState, useEffect } from 'react';
import apiService from '../services/api.service';
import employeeAuthService from '../services/employee-auth.service';
import { useNavigate } from 'react-router-dom';
import './EmployeeImageUpload.css';
import { galleryCategories } from '../constants/galleryCategories';

const EmployeeImageUpload = () => {
  const navigate = useNavigate();
  
  // State variables
  const [years, setYears] = useState([new Date().getFullYear()]); // Current year
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [months, setMonths] = useState([]);
  const [days, setDays] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('Vitrin');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [monthCounts, setMonthCounts] = useState({});
  const [dayCounts, setDayCounts] = useState({});
  const [deletingImageId, setDeletingImageId] = useState(null);
  const [employeeInfo, setEmployeeInfo] = useState(null);

  // Month names in Turkish
  const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];

  // Helper function to get days in a month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  // Check authentication and fetch years on component mount
  useEffect(() => {
    const checkAuthAndFetchData = async () => {
      // Check if employee is logged in
      if (!employeeAuthService.isLoggedIn()) {
        navigate('/employee/login');
        return;
      }
      
      // Get employee info
      const empInfo = employeeAuthService.getUser();
      setEmployeeInfo(empInfo);
      
      // Initialize with current date
      initializeWithCurrentDate();
    };

    checkAuthAndFetchData();
  }, [navigate]);

  // Initialize calendar with current date
  const initializeWithCurrentDate = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const currentDay = currentDate.getDate();
    
    // Set selected year, month, day
    setSelectedYear(currentYear);
    
    // Generate months for the current year
    const monthsArray = Array.from({ length: 12 }, (_, i) => i + 1);
    setMonths(monthsArray);
    
    // Generate days for the current month
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    setDays(daysArray);
    
    // Set selected month and day
    setSelectedMonth(currentMonth);
    setSelectedDay(currentDay);
    
    // Fetch images for today
    fetchImagesForDay(currentYear, currentMonth, currentDay);
    
    // Fetch month and day counts
    fetchDaysData(currentYear, currentMonth);
  };

  // Select a month
  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    setSelectedDay(null);
    setImages([]);
    setError(null);
    
    // Generate days for the selected month
    const daysInMonth = getDaysInMonth(selectedYear, month);
    const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    setDays(daysArray);
    
    // Fetch data for this month
    fetchDaysData(selectedYear, month);
  };

  // Select a day
  const handleDayClick = (day) => {
    setSelectedDay(day);
    setError(null);
    setUploadSuccess(false);
    
    // Fetch images for this day
    fetchImagesForDay(selectedYear, selectedMonth, day);
  };

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
      setError(null); // Clear any previous errors
    }
  };

  // Handle category selection
  const handleCategoryChange = (e) => {
    setUploadCategory(e.target.value);
  };

  // Handle API errors and token expiration
  const handleApiError = (err, defaultMessage) => {
    console.error('API error:', err);
    
    // Check if unauthorized (token expired or invalid)
    if (err.response && (err.response.status === 401 || err.response.status === 403)) {
      // Redirect to login
      navigate('/employee/login');
      return;
    }
    
    // Display error message
    setError(err.response?.data?.error || defaultMessage);
  };

  // Handle image upload
  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadFile) {
      setError('Lütfen bir dosya seçin.');
      return;
    }
    
    if (!selectedYear || !selectedMonth || !selectedDay) {
      setError('Lütfen bir tarih seçin.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setUploadSuccess(false);
    
    try {
      // Create the form data
      const formData = new FormData();
      formData.append('image', uploadFile);
      formData.append('category', uploadCategory);
      formData.append('year', selectedYear.toString());
      formData.append('month', selectedMonth.toString());
      formData.append('day', selectedDay.toString());
      
      // Add employee info
      if (employeeInfo && employeeInfo.id) {
        formData.append('employeeId', employeeInfo.id.toString());
      }
      
      // apiService.post için özel headers
      const config = {
        headers: { 'Content-Type': 'multipart/form-data' }
      };
      
      // Send the request
      const response = await apiService.post('/api/gallery/upload', formData, config);
      
      // Success
      console.log('Yükleme başarılı:', response.data);
      setUploadSuccess(true);
      
      // Refresh images after upload
      fetchImagesForDay(selectedYear, selectedMonth, selectedDay);
      
      // Also refresh the day counts
      fetchDaysData(selectedYear, selectedMonth);
      
      // Reset file input
      setUploadFile(null);
      document.getElementById('employee-file-upload').value = '';
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
    } catch (err) {
      handleApiError(err, 'Görsel yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Fetch days data
  const fetchDaysData = async (year, month) => {
    setLoading(true);
    try {
      const response = await apiService.get(`/api/gallery/days?year=${year}&month=${month}`);
      const dayData = response.data;
      
      // Create a map of day -> count
      const countMap = {};
      dayData.forEach(item => {
        countMap[item.day] = item.count;
      });
      
      setDayCounts(countMap);
      setError(null);
    } catch (err) {
      // Don't set error to UI for this one - we'll still show all days
      console.error('Error fetching days:', err);
      handleApiError(err, null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch images for a specific day
  const fetchImagesForDay = async (year, month, day) => {
    setLoading(true);
    setImages([]);
    try {
      // Ensure month and day are padded with leading zeros if needed
      const paddedMonth = String(month).padStart(2, '0');
      const paddedDay = String(day).padStart(2, '0');
      
      const response = await apiService.get(`/api/gallery/images?year=${year}&month=${paddedMonth}&day=${paddedDay}`);
      setImages(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching images:', err);
      handleApiError(err, 'Görseller yüklenirken bir hata oluştu');
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  // Render calendar navigation
  const renderCalendar = () => {
    return (
      <div className="employee-calendar-container">
        <div className="employee-calendar-header">
          <h3>Tarih Seçin</h3>
          <div className="employee-date-display">
            <span className="employee-selected-date">
              {selectedDay} {monthNames[selectedMonth - 1]} {selectedYear}
            </span>
          </div>
        </div>
        
        {/* Month selector */}
        <div className="employee-months-selector">
          <div className="employee-selector-heading">Ay</div>
          <div className="employee-month-buttons">
            {months.map(month => (
              <button
                key={month}
                className={`employee-month-button ${selectedMonth === month ? 'active' : ''}`}
                onClick={() => handleMonthClick(month)}
              >
                {monthNames[month - 1].slice(0, 3)}
              </button>
            ))}
          </div>
        </div>
        
        {/* Day selector */}
        <div className="employee-days-selector">
          <div className="employee-selector-heading">Gün</div>
          <div className="employee-days-grid">
            {days.map(day => (
              <button
                key={day}
                className={`employee-day-button ${selectedDay === day ? 'active' : ''}`}
                onClick={() => handleDayClick(day)}
              >
                {day}
                {dayCounts[day] > 0 && <span className="employee-day-count">{dayCounts[day]}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render images view with upload form
  const renderImagesAndUpload = () => {
    // Get API URL from environment or default
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
    
    return (
      <div className="employee-gallery-container">
        {/* Upload form */}
        <div className="employee-upload-form-container">
          <h3>Yeni Görsel Yükle</h3>
          <form onSubmit={handleUpload} className="employee-upload-form">
            <div className="employee-form-group">
              <label htmlFor="employee-category">Kategori:</label>
              <select 
                id="employee-category" 
                value={uploadCategory} 
                onChange={handleCategoryChange}
                className="employee-form-control"
              >
                {galleryCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="employee-form-group employee-file-group">
              <label htmlFor="employee-file-upload">Görsel Seç:</label>
              <div className="employee-file-input-container">
                <input 
                  type="file" 
                  id="employee-file-upload" 
                  accept="image/*"
                  onChange={handleFileChange} 
                  className="employee-file-input"
                />
                <button 
                  type="button" 
                  className="employee-file-button"
                  onClick={() => document.getElementById('employee-file-upload').click()}
                >
                  {uploadFile ? 'Görsel Seçildi' : 'Görsel Seç'}
                </button>
                {uploadFile && <span className="employee-file-name">{uploadFile.name}</span>}
              </div>
            </div>
            <div className="employee-form-group">
              <button 
                type="submit" 
                className="employee-upload-button"
                disabled={loading || !uploadFile}
              >
                {loading ? 'Yükleniyor...' : 'Yükle'}
              </button>
            </div>
          </form>
          
          {/* Error/Success messages */}
          {error && <div className="employee-error-message">{error}</div>}
          {uploadSuccess && <div className="employee-success-message">Görsel başarıyla yüklendi!</div>}
        </div>
        
        {/* Image gallery */}
        <div className="employee-image-display">
          <h3>
            {selectedDay} {monthNames[selectedMonth - 1]} {selectedYear} - Yüklenen Görseller
            {loading && <span className="employee-loading-indicator"> (Yükleniyor...)</span>}
          </h3>
          
          <div className="employee-image-grid">
            {loading && images.length === 0 ? (
              <div className="employee-loading-message">Görseller yükleniyor...</div>
            ) : images.length > 0 ? (
              images.map(image => (
                <div key={image.id} className="employee-image-item">
                  {/* Image with correct URL */}
                  <img 
                    src={`${API_URL}${image.file_path}`} 
                    alt={image.category}
                    onError={(e) => {
                      console.error('Image failed to load:', image.file_path);
                      e.target.src = `${API_URL}/uploads/error.png`;
                      e.target.alt = 'Görsel yüklenemedi';
                    }}
                  />
                  <div className="employee-image-details">
                    <span className="employee-image-category">{image.category}</span>
                    <span className="employee-image-uploader">
                      {image.uploader_name || image.employee_name || 'Çalışan'}
                    </span>
                    <span className="employee-image-date">
                      {image.createdAt
                        ? new Date(image.createdAt).toLocaleString('tr-TR')
                        : new Date(image.upload_date).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="employee-no-images">Bu tarih için yüklenmiş görsel bulunmamaktadır.</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Main render function
  return (
    <div className="employee-image-upload-component">
      <div className="employee-section-header">
        <h2>Görsel Yükleme</h2>
      </div>
      
      <div className="employee-content-layout">
        {renderCalendar()}
        {renderImagesAndUpload()}
      </div>
    </div>
  );
};

export default EmployeeImageUpload;
